import type { SettingsState } from "@triedge/staff-settings-domain";
import {
  formatAdmissionsTuitionContractCurrency,
  formatAdmissionsTuitionContractGradeLabel,
  isAdmissionsTuitionConfigurationParentVisible,
  type AdmissionsTuitionConfiguration,
  type AdmissionsTuitionContractViewModel,
} from "@triedge/students-families-domain";
import type { ResolvedRegistrationFee } from "../../../settings/registrationFee";
import type { TuitionContractContext } from "./tuitionContractTemplate";

type DomainRecord = Record<string, unknown>;

export type TuitionContractSnapshotTarget = {
  applicationId: string;
  familyName: string;
  childApplicationId: string;
};

export type TuitionContractSchoolPublicInfo = {
  registrationFee?: Pick<ResolvedRegistrationFee, "descriptionLabel"> | null;
  schoolAddress?: string | null;
  schoolPhone?: string | null;
  schoolPhoneDisplay?: string | null;
  schoolEmail?: string | null;
};

export type TuitionContractSnapshot = {
  eligibleChildApplicationIds: string[];
  studentRows: Array<{
    childName: string;
    gradeLabel: string;
    tuitionLabel: string;
  }>;
  additionalFees: Array<{
    label: string;
    rateLabel: string;
    scheduleLabel: string;
    totalLabel: string;
  }>;
  tuitionSubtotalCents: number;
  totalFeesCents: number;
  totalObligationCents: number;
  registrationFeeDescription: string;
  renderContext: TuitionContractContext;
};

export type TuitionContractSnapshotBaseInput = {
  admissionSchoolYear: string;
  parentSalutation: string;
  streetAddress: string;
  cityStateZip: string;
  registrationFeeDescription: string;
  schoolAddress: string;
  schoolPhone: string;
  schoolEmail: string;
};

export type BuildTuitionContractSnapshotInput = {
  selectedTarget: TuitionContractSnapshotTarget | null;
  application: DomainRecord;
  settingsState: SettingsState;
  admissionSchoolYear: string;
  schoolPublicInfo: TuitionContractSchoolPublicInfo;
  currentDateProvider?: () => Date;
};

const asRecord = (value: unknown): DomainRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as DomainRecord)
    : {};

const asArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const readText = (value: unknown): string => String(value ?? "").trim();

export const formatTuitionContractCurrencyLabel = (
  amountCents: number,
): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(amountCents) ? amountCents / 100 : 0);

const parseCurrencyCents = (value: unknown): number => {
  const raw = readText(value).replace(/[^0-9.-]+/g, "");
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
};

const formatLongDate = (value: Date): string =>
  value.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatContractDueDate = (value: unknown): string | null => {
  const raw = readText(value);
  if (!raw) return null;
  const localDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parsed = localDateMatch
    ? new Date(
        Number(localDateMatch[1]),
        Number(localDateMatch[2]) - 1,
        Number(localDateMatch[3]),
      )
    : new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return formatLongDate(parsed);
};

const buildFeeScheduleLabel = (fee: DomainRecord): string => {
  const dueDate = formatContractDueDate(fee.dueDate ?? fee.defaultDueDate);
  if (dueDate) {
    return `Due ${dueDate}`;
  }
  return readText(fee.rateDescription) || "School office schedule applies";
};

export const parseAdmissionsTuitionConfiguration = (
  value: unknown,
): AdmissionsTuitionConfiguration | null => {
  const record = asRecord(value);
  if (!record.version || !Array.isArray(record.children) || !record.totals) {
    return null;
  }
  return record as AdmissionsTuitionConfiguration;
};

export type CanonicalTuitionContractSigningTerms = {
  paymentAmountLabel: string;
  installmentCount: string;
  installmentAmountLabel: string;
  installmentStartDate: string;
  installmentEndDate: string;
};

export type TuitionContractPaymentMethod =
  | "full"
  | "electronic"
  | "post_dated_checks"
  | "third_party_checks";

const tuitionContractPaymentMethods = new Set<TuitionContractPaymentMethod>([
  "full",
  "electronic",
  "post_dated_checks",
  "third_party_checks",
]);

export const resolveCompatibleTuitionContractPaymentMethod = (
  value: unknown,
  installmentCount: unknown,
): TuitionContractPaymentMethod => {
  const usesInstallments = Number(installmentCount) > 1;
  const fallback = usesInstallments ? "electronic" : "full";
  const normalized = readText(value) as TuitionContractPaymentMethod;
  if (!tuitionContractPaymentMethods.has(normalized)) {
    return fallback;
  }
  return usesInstallments && normalized === "full" ? fallback : normalized;
};

export const resolveCanonicalTuitionContractSigningTerms = (
  value: unknown,
): CanonicalTuitionContractSigningTerms | null => {
  const configuration = parseAdmissionsTuitionConfiguration(value);
  if (!configuration) {
    return null;
  }
  const totalObligationCents = Number(
    configuration.totals?.totalObligationCents,
  );
  const installmentCount = Number(configuration.paymentPlan?.installmentCount);
  const installmentAmountCents = Number(
    configuration.paymentPlan?.installmentAmountCents,
  );
  if (
    !Number.isSafeInteger(totalObligationCents) ||
    totalObligationCents < 0 ||
    !Number.isSafeInteger(installmentCount) ||
    installmentCount < 0 ||
    !Number.isSafeInteger(installmentAmountCents) ||
    installmentAmountCents < 0
  ) {
    return null;
  }
  return {
    paymentAmountLabel:
      formatAdmissionsTuitionContractCurrency(totalObligationCents),
    installmentCount: String(installmentCount),
    installmentAmountLabel: formatAdmissionsTuitionContractCurrency(
      installmentAmountCents,
    ),
    installmentStartDate: readText(configuration.paymentPlan.startDate),
    installmentEndDate: readText(configuration.paymentPlan.endDate),
  };
};

const isAdmissionsTuitionContractViewModel = (
  value: unknown,
): value is AdmissionsTuitionContractViewModel => {
  const record = asRecord(value);
  return Boolean(
    readText(record.academicYear) &&
      Array.isArray(record.studentRows) &&
      Array.isArray(record.feeRows) &&
      readText(record.totalObligationLabel),
  );
};

const findLatestSentContractModel = (
  configuration: AdmissionsTuitionConfiguration,
): AdmissionsTuitionContractViewModel | null => {
  const versions = asArray<DomainRecord>(
    (configuration.contract as DomainRecord).versions,
  )
    .filter((entry) => {
      const status = readText(entry.status);
      return (
        (status === "sent" || status === "signed") &&
        isAdmissionsTuitionContractViewModel(entry.contractModel)
      );
    })
    .sort((left, right) => {
      const leftTime =
        Date.parse(
          readText(left.signedAt) ||
            readText(left.sentAt) ||
            readText(left.generatedAt),
        ) || 0;
      const rightTime =
        Date.parse(
          readText(right.signedAt) ||
            readText(right.sentAt) ||
            readText(right.generatedAt),
        ) || 0;
      return rightTime - leftTime;
    });
  const model = versions[0]?.contractModel;
  return isAdmissionsTuitionContractViewModel(model) ? model : null;
};

const parseInstallmentSummary = (
  value: string,
): {
  count: string;
  amount: string;
  startDate: string;
  endDate: string;
} => {
  const remainderMatch = value.match(
    /^(\d+)\s+payments?\s+of\s+(.+?)\s+and\s+a\s+final\s+payment\s+of\s+.+?(?:\s+from\s+(.+?)\s+to\s+(.+))?$/i,
  );
  if (remainderMatch) {
    return {
      count: String(Number(remainderMatch[1]) + 1),
      amount: readText(remainderMatch[2]) || "____________",
      startDate: readText(remainderMatch[3]) || "____________",
      endDate: readText(remainderMatch[4]) || "____________",
    };
  }
  const match = value.match(
    /^(\d+)\s+payments?\s+of\s+(.+?)(?:\s+from\s+(.+?)\s+to\s+(.+))?$/i,
  );
  if (!match) {
    return {
      count: "_____",
      amount: "____________",
      startDate: "____________",
      endDate: "____________",
    };
  }
  return {
    count: readText(match[1]) || "_____",
    amount: readText(match[2]) || "____________",
    startDate: readText(match[3]) || "____________",
    endDate: readText(match[4]) || "____________",
  };
};

const buildTuitionContractSnapshotFromContractModel = (
  model: AdmissionsTuitionContractViewModel,
  base: TuitionContractSnapshotBaseInput,
): TuitionContractSnapshot => {
  const additionalFees = model.feeRows.map((fee) => ({
    label: readText(fee.label) || "Configured fee",
    rateLabel: readText(fee.label) || "Configured fee",
    scheduleLabel:
      readText(fee.scheduleLabel) || "School office schedule applies",
    totalLabel: readText(fee.amountLabel) || "$0.00",
  }));
  const installment = parseInstallmentSummary(
    readText(model.installmentSummaryLabel),
  );
  const familyAddressLines = model.familyAddressLines.filter((line) =>
    readText(line),
  );
  return {
    eligibleChildApplicationIds: model.studentRows
      .map((row) => readText(row.id))
      .filter(Boolean),
    studentRows: model.studentRows.map((row) => ({
      childName: readText(row.name) || "Student",
      gradeLabel: readText(row.gradeLabel) || "Grade on file",
      tuitionLabel: readText(row.tuitionLabel) || "$0.00",
    })),
    additionalFees,
    tuitionSubtotalCents: parseCurrencyCents(model.tuitionSubtotalLabel),
    totalFeesCents: parseCurrencyCents(model.totalFeesLabel),
    totalObligationCents: parseCurrencyCents(model.totalObligationLabel),
    registrationFeeDescription: base.registrationFeeDescription,
    renderContext: {
      academicYear: readText(model.academicYear) || base.admissionSchoolYear,
      currentDate:
        readText(model.currentDateLabel) || formatLongDate(new Date()),
      parentSalutation:
        readText(model.parentSalutation) || base.parentSalutation,
      streetAddress:
        readText(familyAddressLines[0]) ||
        base.streetAddress ||
        "Address on file",
      cityStateZip:
        readText(familyAddressLines.slice(1).join(", ")) ||
        base.cityStateZip ||
        "City, State ZIP on file",
      studentRows: model.studentRows.map((row) => ({
        childName: readText(row.name) || "Student",
        gradeLabel: readText(row.gradeLabel) || "Grade on file",
        tuitionLabel: readText(row.tuitionLabel) || "$0.00",
      })),
      tuitionSubtotalLabel: readText(model.tuitionSubtotalLabel) || "$0.00",
      multiChildDiscountLabel:
        readText(model.multiChildDiscountLabel) || "$0.00",
      totalTuitionLabel: readText(model.totalTuitionLabel) || "$0.00",
      additionalFees,
      totalFeesLabel: readText(model.totalFeesLabel) || "$0.00",
      totalObligationLabel: readText(model.totalObligationLabel) || "$0.00",
      registrationFeeDescription: base.registrationFeeDescription,
      installmentCountLabel: installment.count,
      installmentAmountLabel: installment.amount,
      installmentStartDateLabel: installment.startDate,
      installmentEndDateLabel: installment.endDate,
      schoolAddress:
        readText(model.schoolAddress) ||
        base.schoolAddress ||
        "School address on file",
      schoolPhone:
        readText(model.schoolPhone) ||
        base.schoolPhone ||
        "School phone on file",
      schoolEmail:
        readText(model.schoolEmail) ||
        base.schoolEmail ||
        "School email on file",
    },
  };
};

export const buildTuitionContractSnapshotFromAdmissionsConfiguration = (
  configuration: AdmissionsTuitionConfiguration,
  base: TuitionContractSnapshotBaseInput,
  currentDateProvider: () => Date = () => new Date(),
): TuitionContractSnapshot | null => {
  const sentContractModel = findLatestSentContractModel(configuration);
  if (sentContractModel) {
    return buildTuitionContractSnapshotFromContractModel(
      sentContractModel,
      base,
    );
  }

  if (!isAdmissionsTuitionConfigurationParentVisible(configuration)) {
    return null;
  }

  const children = asArray<DomainRecord>(configuration.children).filter(
    (child) => readText(child.childApplicationId),
  );
  if (children.length === 0) {
    return null;
  }

  const studentRows = children.map((child) => {
    const baseTuition = asRecord(child.baseTuition);
    const currentCents = Math.max(
      0,
      Math.round(Number(baseTuition.currentCents ?? 0)),
    );
    return {
      childApplicationId: readText(child.childApplicationId),
      childName: readText(child.name) || "Student",
      gradeLabel: formatAdmissionsTuitionContractGradeLabel(child.gradeLabel),
      tuitionLabel: formatTuitionContractCurrencyLabel(currentCents),
    };
  });

  const additionalFees = children.flatMap((child) =>
    asArray<DomainRecord>(child.fees)
      .map((fee) => {
        const amount = asRecord(fee.amount);
        const totalCents = Math.max(
          0,
          Math.round(Number(amount.currentCents ?? 0)),
        );
        if (totalCents <= 0) {
          return null;
        }
        return {
          label: readText(fee.name) || "Configured fee",
          rateLabel: readText(fee.name) || "Configured fee",
          scheduleLabel: buildFeeScheduleLabel(fee),
          totalLabel: formatTuitionContractCurrencyLabel(totalCents),
        };
      })
      .filter((fee): fee is NonNullable<typeof fee> => Boolean(fee)),
  );

  const totals = asRecord(configuration.totals);
  const tuitionSubtotalCents = Math.max(
    0,
    Math.round(Number(totals.tuitionSubtotalCents ?? 0)),
  );
  const totalTuitionCents = Math.max(
    0,
    Math.round(Number(totals.totalTuitionCents ?? tuitionSubtotalCents)),
  );
  const totalFeesCents = Math.max(
    0,
    Math.round(Number(totals.totalFeesCents ?? 0)),
  );
  const totalObligationCents = Math.max(
    0,
    Math.round(
      Number(totals.totalObligationCents ?? totalTuitionCents + totalFeesCents),
    ),
  );
  const paymentPlan = asRecord(configuration.paymentPlan);
  const installmentCount = Math.max(
    0,
    Math.round(Number(paymentPlan.installmentCount ?? 0)),
  );
  const installmentAmountCents = Math.max(
    0,
    Math.round(Number(paymentPlan.installmentAmountCents ?? 0)),
  );
  const installmentStartDate =
    formatContractDueDate(paymentPlan.startDate) ||
    readText(paymentPlan.startDate);
  const installmentEndDate =
    formatContractDueDate(paymentPlan.endDate) || readText(paymentPlan.endDate);

  return {
    eligibleChildApplicationIds: studentRows.map(
      (row) => row.childApplicationId,
    ),
    studentRows: studentRows.map((row) => ({
      childName: row.childName,
      gradeLabel: row.gradeLabel,
      tuitionLabel: row.tuitionLabel,
    })),
    additionalFees,
    tuitionSubtotalCents,
    totalFeesCents,
    totalObligationCents,
    registrationFeeDescription: base.registrationFeeDescription,
    renderContext: {
      academicYear: configuration.academicYear || base.admissionSchoolYear,
      currentDate: formatLongDate(currentDateProvider()),
      parentSalutation: base.parentSalutation,
      streetAddress: base.streetAddress || "Address on file",
      cityStateZip: base.cityStateZip || "City, State ZIP on file",
      studentRows: studentRows.map((row) => ({
        childName: row.childName,
        gradeLabel: row.gradeLabel,
        tuitionLabel: row.tuitionLabel,
      })),
      tuitionSubtotalLabel:
        formatTuitionContractCurrencyLabel(tuitionSubtotalCents),
      multiChildDiscountLabel: formatTuitionContractCurrencyLabel(
        Number(totals.multiChildDiscountCents ?? 0),
      ),
      totalTuitionLabel: formatTuitionContractCurrencyLabel(totalTuitionCents),
      additionalFees,
      totalFeesLabel: formatTuitionContractCurrencyLabel(totalFeesCents),
      totalObligationLabel:
        formatTuitionContractCurrencyLabel(totalObligationCents),
      registrationFeeDescription: base.registrationFeeDescription,
      installmentCountLabel:
        installmentCount > 0 ? String(installmentCount) : "_____",
      installmentAmountLabel:
        installmentAmountCents > 0
          ? formatTuitionContractCurrencyLabel(installmentAmountCents)
          : "____________",
      installmentStartDateLabel: installmentStartDate || "____________",
      installmentEndDateLabel: installmentEndDate || "____________",
      schoolAddress: base.schoolAddress || "School address on file",
      schoolPhone: base.schoolPhone || "School phone on file",
      schoolEmail: base.schoolEmail || "School email on file",
    },
  };
};

export const buildTuitionContractSnapshot = ({
  selectedTarget,
  application,
  admissionSchoolYear,
  schoolPublicInfo,
  currentDateProvider = () => new Date(),
}: BuildTuitionContractSnapshotInput): TuitionContractSnapshot | null => {
  if (!selectedTarget) return null;

  const applicantRecord = asRecord(application.applicant);
  const primaryContact = asRecord(applicantRecord.primaryContact);
  const primaryAddress = asRecord(primaryContact.address);
  const parentFullName = [
    readText(primaryContact.firstName),
    readText(primaryContact.lastName),
  ]
    .filter(Boolean)
    .join(" ");
  const streetAddress = readText(primaryAddress.street);
  const cityStateZip = [
    readText(primaryAddress.city),
    readText(primaryAddress.state),
    readText(primaryAddress.zipCode),
  ]
    .filter(Boolean)
    .join(", ")
    .replace(/, (?=[^,]+$)/, " ");
  const registrationFeeDescription =
    schoolPublicInfo.registrationFee?.descriptionLabel ||
    "the configured registration fee";
  const baseSnapshotInput: TuitionContractSnapshotBaseInput = {
    admissionSchoolYear,
    parentSalutation: parentFullName || `${selectedTarget.familyName} Family`,
    streetAddress,
    cityStateZip,
    registrationFeeDescription,
    schoolAddress: schoolPublicInfo.schoolAddress || "School address on file",
    schoolPhone:
      schoolPublicInfo.schoolPhoneDisplay ||
      schoolPublicInfo.schoolPhone ||
      "School phone on file",
    schoolEmail: schoolPublicInfo.schoolEmail || "School email on file",
  };
  const admissionsTuitionConfiguration = parseAdmissionsTuitionConfiguration(
    application.tuitionConfiguration,
  );
  if (admissionsTuitionConfiguration) {
    const admissionsSnapshot =
      buildTuitionContractSnapshotFromAdmissionsConfiguration(
        admissionsTuitionConfiguration,
        baseSnapshotInput,
        currentDateProvider,
      );
    return admissionsSnapshot;
  }

  return null;
};
